<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900px">
     <v-card>
         <v-card-title>Import AOIs</v-card-title>
         <v-card-text>
             <!-- Project/Image Selectors -->
             <v-row>
                 <v-col>
                    <v-autocomplete
                        v-model="selectedProject"
                        :items="projects"
                        :loading="projectLoading"
                        :search-input.sync="oculisProjectSearch"
                        item-value="id"
                        item-text="text"
                        label="Search Projects..."
                        outlined
                        dense
                        persistent-hint
                        hint="Type at least 3 letters..."
                        class="mr-2"
                        @change="oculisProjectSearch=''"
                        return-object>
                            <template #no-data>
                                <p class="ml-2" style="color: gray">No results found</p>
                            </template>
                            <template v-slot:item="data">
                                <v-list-item-content>
                                    <v-list-item-title>{{ data.item.job_number }} {{data.item.job_name}} - {{data.item.country}}</v-list-item-title>
                                    <v-list-item-subtitle>{{ data.item.decipher_path }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                    </v-autocomplete>
                </v-col>
                 <v-col>
                    <v-select
                        label="Media"
                        :items="medias"
                        v-model="selectedMedia"
                        item-value="id"
                        item-text="name"
                        :disabled="!selectedProject"
                        :loading="mediasLoading"
                        outlined
                        dense
                        @change="handleImageChange"
                        return-object
                    ></v-select>
                </v-col>
             </v-row>
             <!-- Preview Canvas -->
             <div class="preview-area">
                 <v-row v-if="selectedMedia">
                        <v-col cols="12" md="8">
                            <v-subheader>Source Image Preview (Selected AOIs will be highlighted)</v-subheader>
                            <div class="image-container-dialog" ref="sourceImageContainer">
                                <v-img
                                    :src="mediaData.media"
                                    @load="onSourceImageLoad"
                                    contain
                                    class="dialog-preview-image"
                                >
                                    <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0 justify-center">
                                            <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                                        </v-row>
                                    </template>
                                </v-img>
                                <canvas
                                    id="source-drawing-canvas"
                                    ref="sourceDrawingCanvas"
                                ></canvas>
                            </div>
                            <div class="text-center mt-2" v-if="mediaData.aois && mediaData.aois.length > 0">
                                {{ selectedSourceAoiIndices.length }} / {{ mediaData.aois.length }} AOI(s) selected.
                            </div>
                            <div class="text-center mt-2" v-else>
                                No AOIs detected for this image.
                            </div>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-list dense class="aoi-selection-list">
                                <v-subheader>Available AOIs to Import</v-subheader>
                                
                                <v-checkbox
                                    v-model="selectAllAOIs"
                                    :indeterminate="selectedSourceAoiIndices.length > 0 && selectedSourceAoiIndices.length < mediaData.aois.length"
                                    label="Select All"
                                    hide-details
                                ></v-checkbox>
                                <v-list-item-group
                                    multiple
                                    v-model="selectedSourceAoiIndices"
                                >
                                    <v-list-item
                                        v-for="(aoi, index) in mediaData.aois"
                                        :key="`source-aoi-${index}`"
                                        :value="index"
                                        :style="{ color: aoi.color }"
                                    >
                                        <template v-slot:default="{ active }">
                                            <v-list-item-action>
                                                <v-checkbox :input-value="active"></v-checkbox>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ aoi.name || `AOI ${index + 1}` }} ({{ aoi.shape }})
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                    </v-list-item>
                                </v-list-item-group>
                                <v-list-item v-if="!mediaData.aois || mediaData.aois.length === 0">
                                    <v-list-item-content>
                                        <v-list-item-title class="text--disabled">No AOIs available.</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                            
                            <v-list dense class="aoi-selection-list">
                                <v-subheader>Available Nets to Import</v-subheader>
                                
                                <v-checkbox
                                    v-model="selectAllNets"
                                    :indeterminate="selectedSourceNetIndices.length > 0 && selectedSourceNetIndices.length < mediaData.nets.length"
                                    label="Select All"
                                    hide-details
                                ></v-checkbox>
                                <v-list-item-group
                                    multiple
                                    v-model="selectedSourceNetIndices"
                                >
                                    <v-list-item
                                        v-for="(net, index) in mediaData.nets"
                                        :key="`source-net-${index}`"
                                        :value="index"
                                        :style="{ color: net.color }"
                                    >
                                        <template v-slot:default="{ active }">
                                            <v-list-item-action>
                                                <v-checkbox :input-value="active"></v-checkbox>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ net.name || `Net ${index + 1}` }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                    </v-list-item>
                                </v-list-item-group>
                                <v-list-item v-if="!mediaData.nets || mediaData.nets.length === 0">
                                    <v-list-item-content>
                                        <v-list-item-title class="text--disabled">No Nets available.</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-col>
                    </v-row>
                    <div v-else class="text-center pa-4 text--disabled">
                        Please select a project and an image to see a preview.
                    </div>
             </div>
         </v-card-text>
         <v-card-actions>
             <v-btn text @click="$emit('input', false)">Cancel</v-btn>
             <v-btn color="primary" @click="importAois">Import</v-btn>
         </v-card-actions>
     </v-card>
     
        <v-snackbar v-model="isSnackbarVisible">
            {{ snackBarMessage }}
        </v-snackbar>
  </v-dialog>
</template>
<script>
import useOculis from '../../useOculis'
import { ref, watch, computed, getCurrentInstance, nextTick } from '@vue/composition-api'

export default {
    props: ['value', 'currentImage'],
    setup(props, { emit }) {
        const vm = getCurrentInstance().proxy
        const projectSearch = ref("")
        const selectedProject = ref(null)
        const selectedMedia = ref(null)

        const sourceDrawingCanvas = ref(null);
        const sourceImageContainer = ref(null); // Ref for the div containing source image and canvas
        let sourceCtx = null;

        // Scaling factors for the source image preview
        const sourceImageRenderedWidth = ref(0);
        const sourceImageRenderedHeight = ref(0);
        const sourceScaleX = ref(1);
        const sourceScaleY = ref(1);
        const selectedSourceAoiIndices = ref([]); // Store indices of AOIs to import
        const selectedSourceNetIndices = ref([]); // Store indices of Nets to import

        const isSnackbarVisible = ref(false)
        const snackBarMessage = ref("")

        const {
            projectsData, projectLoading,
            mediasLoading, medias, mediaData, oculisProjectSearch,
            fetchOculisProjects, fetchMediasAOIs, fetchMedia
        } = useOculis()


        
        // watch(
        //     projectSearch,
        //     async (val, oldVal) => {
        //         if (val != null && val.length > 2 && selectedProject.value == null) {
        //             selectedMedia.value = null
        //             projectLoading.value = true
        //             await fetchOculisProjects({search: val})

        //             projectLoading.value = false
        //         }
        //     }
        // )
        watch(
            selectedProject,
            async () => {
                selectedMedia.value = null
                fetchMediasAOIs(selectedProject.value.id)
            }
        )
        watch(() => selectedMedia, () => {
            if (sourceDrawingCanvas.value) {
                const ctx = sourceDrawingCanvas.value.getContext('2d');
                ctx.clearRect(0, 0, sourceDrawingCanvas.value.width, sourceDrawingCanvas.value.height);
                // Temporarily hide or zero out canvas until load finishes
                sourceDrawingCanvas.value.width = 0;
                sourceDrawingCanvas.value.height = 0;
            }
        });

        const projects = computed(() => projectsData.value.map(project => {
            return {
                text: `${project.job_number} ${project.job_name} - ${project.country}`,
                ...project
                }
            }).flat()
        )
        
        const handleProjectChange = () => {
            selectedMedia.value = null; // Reset image selection when project changes
            mediaData.value = {}
            selectedSourceAoiIndices.value = []; // Clear selected AOIs
        };

        const handleImageChange = async () => {
            await fetchMedia({id: selectedMedia.value.id})
            selectedSourceAoiIndices.value = []; // Clear selected AOIs when image changes
            // Defer canvas initialization to next tick to ensure v-img has rendered
            vm.$nextTick(() => {
                onSourceImageLoad();
            });
        };
       
        const onSourceImageLoad = async () => {
             await nextTick();

             // Use setTimeout or $nextTick to ensure the image and its parent div are rendered
            // and have correct dimensions before trying to get clientWidth/Height.
            if (!mediaData.value || !sourceDrawingCanvas.value || !sourceImageContainer.value) return;

            const containerRect = sourceImageContainer.value.getBoundingClientRect();
            const containerW = containerRect.width;
            const containerH = containerRect.height;

            // 2. Get Natural Image Dimensions
            const naturalW = mediaData.value.width || 0;
            const naturalH = mediaData.value.height || 0;

            if (naturalW === 0 || naturalH === 0) return;

            // 3. Calculate "Contain" Logic manually
            // This determines exactly how big the image is rendered visually
            const scale = Math.min(
                containerW / naturalW, 
                containerH / naturalH
            );

            const renderedW = naturalW * scale;
            const renderedH = naturalH * scale;

            // 4. Calculate Offsets to Center the Canvas
            // (Container Size - Image Size) / 2
            const offsetX = (containerW - renderedW) / 2;
            const offsetY = (containerH - renderedH) / 2;

            // 5. Update Canvas Dimensions & Position
            const canvas = sourceDrawingCanvas.value;
            
            // Set internal resolution (coordinate system)
            canvas.width = renderedW;
            canvas.height = renderedH;

            // Set CSS positioning to match the centered image
            canvas.style.position = 'absolute'; // Ensure this is set
            canvas.style.width = `${renderedW}px`;
            canvas.style.height = `${renderedH}px`;
            canvas.style.left = `${offsetX}px`;
            canvas.style.top = `${offsetY}px`;

            // 6. Save Scales for later drawing logic
            // Now 0,0 on canvas equals 0,0 on the visual image, 
            // so we just map rendered size to natural size.
            sourceScaleX.value = naturalW / renderedW;
            sourceScaleY.value = naturalH / renderedH;
            
            sourceCtx = sourceDrawingCanvas.value.getContext('2d');

            drawAOIs(); // Pass offsets for drawing
        };
        const drawAOIs = () => {
            if (!sourceCtx || !mediaData.value || !sourceDrawingCanvas.value) return;

            // Clear the canvas completely
            sourceCtx.clearRect(0, 0, sourceDrawingCanvas.value.width, sourceDrawingCanvas.value.height);

            const aoisToDraw = mediaData.value.aois || [];

            aoisToDraw.forEach((originalZone, index) => {
                const isSelected = selectedSourceAoiIndices.value.includes(index);

                let renderableZone = { 
                    ...originalZone, 
                    color: originalZone.color, 
                    opacity: originalZone.opacity,
                    shape: originalZone.shape
                };

                // Helper to map X/Y with scaling and offset
                const mapX = (x) => (x / sourceScaleX.value);
                const mapY = (y) => (y / sourceScaleY.value);
                const mapDimX = (w) => w / sourceScaleX.value;
                const mapDimY = (h) => h / sourceScaleY.value;

                if (originalZone.shape === 'rectangle') {
                    renderableZone.x = mapX(originalZone.x);
                    renderableZone.y = mapY(originalZone.y);
                    renderableZone.width = mapDimX(originalZone.width);
                    renderableZone.height = mapDimY(originalZone.height);

                } else if (originalZone.shape === 'circle') {
                    renderableZone.x = mapX(originalZone.x);
                    renderableZone.y = mapY(originalZone.y);
                    // Assuming uniform scaling for circle radius, or using X scale as reference
                    renderableZone.radius = mapDimX(originalZone.radius);

                } else if (originalZone.shape === 'polygon') {
                    renderableZone.points = originalZone.points.map(p => ({
                        x: mapX(p.x),
                        y: mapY(p.y)
                    }));
                }

                // Draw the transformed zone
                drawZoneShape(sourceCtx, renderableZone, isSelected);
            });
        };

        const drawPoint = (ctx, x, y) => {
            ctx.fillStyle = 'blue';
            ctx.beginPath();
            ctx.arc(x, y, 4, 0, 2 * Math.PI);
            ctx.fill();
        };

        const drawZoneShape = (ctx, zone, isSelected = false) => {
            ctx.fillStyle = zone.color;
            ctx.strokeStyle = isSelected ? 'blue' : zone.color;
            ctx.lineWidth = isSelected ? 4 : 2;
            // Use .opacity if available, otherwise default to 0.5, but override if selected
            ctx.globalAlpha = isSelected ? 0.7 : (zone.opacity || 0.5);

            ctx.beginPath();
            if (zone.shape === 'rectangle') {
                ctx.rect(zone.x, zone.y, zone.width, zone.height);
            } else if (zone.shape === 'circle') {
                ctx.arc(zone.x, zone.y, zone.radius, 0, 2 * Math.PI);
            } else if (zone.shape === 'polygon') {
                zone.points.forEach((p, i) => {
                    i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
                });
                ctx.closePath();
            }
            ctx.fill();
            ctx.stroke();
            ctx.globalAlpha = 1;

            // Draw selection handles (Edit Mode)
            // Note: We check 'currentMode' (make sure this ref is available in your scope)
            if (isSelected) {
                if (zone.shape === 'rectangle') {
                    drawPoint(ctx, zone.x, zone.y);
                    drawPoint(ctx, zone.x + zone.width, zone.y);
                    drawPoint(ctx, zone.x, zone.y + zone.height);
                    drawPoint(ctx, zone.x + zone.width, zone.y + zone.height);
                } else if (zone.shape === 'circle') {
                    drawPoint(ctx, zone.x + zone.radius, zone.y);
                    drawPoint(ctx, zone.x - zone.radius, zone.y);
                    drawPoint(ctx, zone.x, zone.y + zone.radius);
                    drawPoint(ctx, zone.x, zone.y - zone.radius);
                } else if (zone.shape === 'polygon') {
                    zone.points.forEach((p) => drawPoint(ctx, p.x, p.y));
                }
            }
        };


        const selectAllAOIs = computed({
            // Getter: Defines how the computed property's value is derived.
            get() {
                if (mediaData.value.aois == undefined) return false
                return selectedSourceAoiIndices.value.length == mediaData.value.aois.length
            },
            // Setter: Defines how to update the underlying reactive data when the computed property is assigned a new value.
            set(newValue) {
                if (newValue) selectedSourceAoiIndices.value = Array.from({ length: mediaData.value.aois.length }, (_, i) => i);
                else selectedSourceAoiIndices.value = [];
            }
        });

        

        const selectAllNets = computed({
            // Getter: Defines how the computed property's value is derived.
            get() {
                if (mediaData.value.nets == undefined) return false
                return selectedSourceNetIndices.value.length == mediaData.value.nets.length
            },
            // Setter: Defines how to update the underlying reactive data when the computed property is assigned a new value.
            set(newValue) {
                if (newValue) selectedSourceNetIndices.value = Array.from({ length: mediaData.value.nets.length }, (_, i) => i);
                else selectedSourceNetIndices.value = [];
            }
        });

        
        const importAois = () => {
            if (!mediaData.value ) {
                snackBarMessage.value = "Please select a source image and ensure a target image is active."
                isSnackbarVisible.value = true
                return;
            }

            if (selectedSourceAoiIndices.value.length === 0 && selectedSourceNetIndices.value.length === 0) {
                snackBarMessage.value = "No AOIs/Nets selected to import."
                isSnackbarVisible.value = true
                return;
            }

            const sourceImageAoIs = mediaData.value.aois;
            const sourceImageNets = mediaData.value.nets;
            const sourceImageWidth = mediaData.value.width;
            const sourceImageHeight = mediaData.value.height;

            // Current target image dimensions (where AOIs will be pasted)
            const targetImageWidth = props.currentImage.width;
            const targetImageHeight = props.currentImage.height;

            if (targetImageWidth === 0 || targetImageHeight.value === 0) {
                
                snackBarMessage.value = "Current target image dimensions are not loaded. Cannot import. Ensure an image is selected and loaded in the main view."
                isSnackbarVisible.value = true
                return;
            }
            if (sourceImageWidth === 0 || sourceImageHeight === 0) {
                snackBarMessage.value = "Source image natural dimensions from the selected project are invalid. Cannot import."
                isSnackbarVisible.value = true
                 return;
            }

            const scaleFactorX = targetImageWidth / sourceImageWidth;
            const scaleFactorY = targetImageHeight / sourceImageHeight;

            const importedAOIs = selectedSourceAoiIndices.value.map(index => {
                const aoi = sourceImageAoIs[index];
                const newZone = JSON.parse(JSON.stringify(aoi)); 

                // Apply scaling
                if (newZone.shape === 'polygon') {
                    newZone.points = newZone.points.map(p => ({
                        x: p.x * scaleFactorX,
                        y: p.y * scaleFactorY
                    }));
                } else if (newZone.shape === 'rectangle') {
                    newZone.x = newZone.x * scaleFactorX;
                    newZone.y = newZone.y * scaleFactorY;
                    newZone.width = newZone.width * scaleFactorX;
                    newZone.height = newZone.height * scaleFactorY;
                } else if (newZone.shape === 'circle') {
                    newZone.x = newZone.x * scaleFactorX;
                    newZone.y = newZone.y * scaleFactorY;
                    newZone.radius = newZone.radius * scaleFactorX; 
                }
                
                newZone._source_id = newZone.id; 

                delete newZone.groupId;
                delete newZone.id; 
                delete newZone.created_at;
                delete newZone.updated_at; 
                newZone.media = props.currentImage.id; 
                newZone.project = props.currentImage.project;
                newZone.name = `${newZone.name || `AOI ${index + 1}`}`; 

                return newZone;
            });

            const importedNets = selectedSourceNetIndices.value.map(index => {
                const net = sourceImageNets[index];
                const newNet = JSON.parse(JSON.stringify(net)); 

                // Clean up the net for the new image
                delete newNet.id;
                delete newNet.created_at;
                delete newNet.updated_at;
                newNet.media = props.currentImage.id;
                
                newNet._source_aoi_ids = net.aois || [];

                return newNet;
            });

            snackBarMessage.value = `${importedAOIs.length} AOI(s) and ${importedNets.length} Net(s) queued for import.`
            isSnackbarVisible.value = true
            
            emit('aois-imported', {
                media: props.currentImage.id,
                project: props.currentImage.project,
                aois: importedAOIs,
                nets: importedNets
            });


            emit('input', false);
        };

        watch(selectedSourceAoiIndices, () => {
            if (selectedMedia.value && sourceDrawingCanvas.value && sourceCtx) {
                onSourceImageLoad(); 
            }
        }, { deep: true });



        return { 
            projects, projectLoading, projectSearch, selectedProject,
            selectedMedia, mediasLoading, medias, mediaData, oculisProjectSearch,
            sourceDrawingCanvas,
            sourceImageContainer,
            sourceImageRenderedWidth,
            sourceImageRenderedHeight,
            sourceScaleX,
            sourceScaleY,
            selectAllAOIs,
            selectAllNets,
            selectedSourceAoiIndices,
            selectedSourceNetIndices,
            isSnackbarVisible,
            snackBarMessage,

            importAois,
            onSourceImageLoad,
            handleProjectChange,
            handleImageChange
        }
    }
}
</script>

<style scoped>

.image-container-dialog {
    position: relative; /* Crucial for layering canvas over image */
    width: 100%; /* Take full width of its column */
    height: 500px; 
    max-height: 500px; /* Limit height of the preview */
    background-color: #f0f0f0; /* Background for when image is smaller */
    display: flex; /* For centering content inside */
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    overflow: hidden; /* Hide overflow from image/canvas */
}
.dialog-preview-image {
    width: 100%;
    height: 100%;
    display: block;
    position: relative; /* Important for v-img, but canvas needs to be absolute wrt parent */
    z-index: 1; /* Place image below canvas for selection */
}
#source-drawing-canvas {
    position: absolute;
    top: 0;
    left: 0;
    background-color: transparent;
    z-index: 2; /* Place canvas above image */
}

.aoi-selection-list {
    max-height: 400px; /* Limit height of the list */
    overflow-y: auto; /* Enable scrolling for the list */
    border: 1px solid #eee;
}

</style>